// src/seo/seo.js
// Per-page <head> tags for a single-page app, with no extra dependencies.
// useSeo({ title, description, canonical, image, robots, jsonLd, type }) sets title, description,
// robots, canonical, Open Graph, Twitter and one JSON-LD block, and puts the previous values back
// when the page unmounts. Pages that come out of scripts/seo-build.mjs (prerendered HTML) already
// carry the same tags for crawlers that don't run JavaScript; this keeps them right after navigation.
import { useEffect } from "react";

export const SITE = "https://routechef.com";
export const absUrl = (p) => (!p ? "" : /^https?:\/\//.test(p) ? p : `${SITE}${p.startsWith("/") ? "" : "/"}${p}`);

const META = [
    ["name", "description", (m) => m.description],
    ["name", "robots", (m) => m.robots || "index,follow,max-image-preview:large"],
    ["property", "og:title", (m) => m.ogTitle || m.title],
    ["property", "og:description", (m) => m.description],
    ["property", "og:type", (m) => m.type || "website"],
    ["property", "og:url", (m) => m.canonical],
    ["property", "og:image", (m) => absUrl(m.image)],
    ["property", "og:site_name", () => "RouteChef"],
    ["property", "og:locale", () => "en_IN"],
    ["name", "twitter:card", (m) => (m.image ? "summary_large_image" : "summary")],
    ["name", "twitter:title", (m) => m.ogTitle || m.title],
    ["name", "twitter:description", (m) => m.description],
    ["name", "twitter:image", (m) => absUrl(m.image)],
];

export function applySeo(m) {
    if (typeof document === "undefined" || !m) return () => {};
    const undo = [];
    const prevTitle = document.title;
    if (m.title) document.title = m.title;
    undo.push(() => { document.title = prevTitle; });

    for (const [attr, key, get] of META) {
        const val = get(m);
        let el = document.head.querySelector(`meta[${attr}="${key}"]`);
        const had = !!el, prev = el?.getAttribute("content");
        if (!val) continue;
        if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
        el.setAttribute("content", val);
        undo.push(() => { if (!had) el.remove(); else if (prev != null) el.setAttribute("content", prev); });
    }
    if (m.canonical) {
        let el = document.head.querySelector('link[rel="canonical"]');
        const had = !!el, prev = el?.getAttribute("href");
        if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
        el.setAttribute("href", m.canonical);
        undo.push(() => { if (!had) el.remove(); else if (prev) el.setAttribute("href", prev); });
    }
    document.getElementById("rc-seo-jsonld")?.remove();
    if (m.jsonLd) {
        const ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "rc-seo-jsonld";
        ld.text = JSON.stringify(m.jsonLd);
        document.head.appendChild(ld);
        undo.push(() => ld.remove());
    }
    return () => undo.reverse().forEach((f) => f());
}

export default function useSeo(meta) {
    const key = meta ? JSON.stringify(meta) : "";
    useEffect(() => (meta ? applySeo(meta) : undefined), [key]); // eslint-disable-line react-hooks/exhaustive-deps
}
